import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/api/client";
import { Radar, UserPlus } from "lucide-react";
import { REGISTER } from "@/constants/testIds";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await register(form);
      navigate("/portal", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Radar className="h-5 w-5" />
          </div>
          <div>
            <p className="font-heading text-lg font-bold leading-none">HCIT-CallCRM</p>
            <p className="mt-1 label-eyebrow leading-none">Customer portal signup</p>
          </div>
        </div>

        <h1 className="font-heading text-3xl font-bold tracking-tight">Create your account</h1>
        <p className="mt-2 text-sm text-muted-foreground">Register as a customer to raise service calls and track tickets.</p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          {[
            { key: "name", label: "Full name", type: "text", tid: REGISTER.nameInput },
            { key: "email", label: "Email", type: "email", tid: REGISTER.emailInput },
            { key: "phone", label: "Phone (optional)", type: "tel" },
            { key: "password", label: "Password", type: "password", tid: REGISTER.passwordInput },
          ].map((f) => (
            <div key={f.key}>
              <label className="label-eyebrow mb-1.5 block">{f.label}</label>
              <input
                data-testid={f.tid}
                type={f.type}
                value={form[f.key]}
                onChange={onChange(f.key)}
                required={f.key !== "phone"}
                className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
          ))}

          {err && <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

          <button
            data-testid={REGISTER.submitButton}
            type="submit"
            disabled={busy}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            <UserPlus className="h-4 w-4" />
            {busy ? "Creating…" : "Create account"}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" data-testid={REGISTER.loginLink} className="font-semibold text-primary hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
